<template>
  <div v-if="visible" class="content-tabs-loading">
    <span class="pi pi-spin pi-spinner text-xl" aria-hidden="true"></span>
    <p>{{ displayText }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateOrValue as translateI18nOrValue } from '@/utils/i18n';
const i18n = useI18n();

const props = defineProps<{
  active: boolean;
  showDelay?: number;
  minDuration?: number;
  text?: string;
}>();

const emit = defineEmits<{
  (e: 'min-duration-complete'): void;
}>();

const showDelay = computed(() => props.showDelay ?? 250);
const minDuration = computed(() => props.minDuration ?? showDelay.value);
const visible = ref(false);
const showTimer = ref<number | null>(null);
const hideTimer = ref<number | null>(null);
const lastShow = ref<number>(0);

const clearShowTimer = () => {
  if (showTimer.value !== null) {
    clearTimeout(showTimer.value);
    showTimer.value = null;
  }
};

const clearHideTimer = () => {
  if (hideTimer.value !== null) {
    clearTimeout(hideTimer.value);
    hideTimer.value = null;
  }
};

const hideAndNotify = () => {
  clearHideTimer();
  visible.value = false;
  emit('min-duration-complete');
};

// Translation logic: if text is a translation key, use t(key), else show as-is
const displayText = computed(() => {
  return translateI18nOrValue(i18n, props.text ?? 'messages.loading');
});

watch(
  () => props.active,
  (val) => {
    if (val) {
      clearHideTimer();
      if (visible.value) return;

      clearShowTimer();
      showTimer.value = window.setTimeout(() => {
        clearShowTimer();
        if (!props.active) return;
        visible.value = true;
        lastShow.value = Date.now();
      }, showDelay.value);
    } else {
      clearShowTimer();

      if (!visible.value) {
        emit('min-duration-complete');
        return;
      }

      const elapsed = Date.now() - lastShow.value;
      const remaining = minDuration.value - elapsed;
      if (remaining > 0) {
        clearHideTimer();
        hideTimer.value = window.setTimeout(() => {
          hideAndNotify();
        }, remaining);
      } else {
        hideAndNotify();
      }
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  clearShowTimer();
  clearHideTimer();
});
</script>

<style scoped>
.content-tabs-loading {
  min-height: 8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}
</style>
